// Shared reading of SterlingForm rows (list, detail panel, home).

export const FORM_TYPE_LABELS = {
    rtb7: 'Rent increase',
    rtb11a: 'Park site rent increase',
    rtb30: 'End of tenancy',
    'inspection-report': 'Inspection report',
};

export const FORM_TYPE_TITLES = {
    rtb7: 'Notice of Rent Increase · RTB-7',
    rtb11a: 'Notice of Rent Increase · Manufactured Home Site · RTB-11a',
    rtb30: 'Notice to End Tenancy · RTB-30',
    'inspection-report': 'Condition inspection report',
};

// Notices that change the rent: same confirm / send / Buildium flow.
export const RENT_INCREASE_TYPES = ['rtb7', 'rtb11a'];

export function isRentIncrease(form) {
    return !!form && RENT_INCREASE_TYPES.includes(form.form_type);
}

export function formMeta(form, key) {
    const m = ((form && form.metas) || []).find(x => x.meta_key === key);
    return m ? m.meta_value : undefined;
}

/** Buildium tenants (FirstName/LastName) or report tenants (first_name / name). */
export function tenantsOf(form) {
    const list = formMeta(form, 'tenants');
    return Array.isArray(list) ? list.filter(t => t && typeof t === 'object') : [];
}

export function tenantName(t) {
    return (t.name || [t.FirstName || t.first_name, t.LastName || t.last_name].filter(Boolean).join(' ') || t.Email || t.email || '').trim();
}

/** Amounts arrive as numbers or as "2,192.43". */
export function formatMoney(value) {
    if (value === undefined || value === null || value === '') return '';
    const n = Number(String(value).replace(/,/g, ''));
    if (!isFinite(n)) return String(value);
    return n.toLocaleString('en-CA', {style: 'currency', currency: 'CAD'}).replace('CA$', '$');
}

/** "01/02/2027" (d/m/Y, as the forms store it) → "Feb 1, 2027". */
export function formatDmy(value) {
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(value || '').trim());
    if (!m) return value || '';
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return d.toLocaleDateString('en-CA', {month: 'short', day: 'numeric', year: 'numeric'});
}

export function formatDate(value) {
    if (!value) return '';
    // A bare date is a calendar day, not UTC midnight (which shows as the day before here).
    const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
    const d = day ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])) : new Date(String(value).replace(' ', 'T'));
    if (isNaN(d.getTime())) return String(value).slice(0, 10);
    return d.toLocaleDateString('en-CA', {month: 'short', day: 'numeric', year: 'numeric'});
}

/** Plain-word status badges: kind is warn | ok | info | muted. */
export function formStatus(form) {
    const out = [];
    const served = formMeta(form, 'served_at');
    const auto = isRentIncrease(form) && !!formMeta(form, 'auto_generated');
    const confirmed = !!formMeta(form, 'pm_confirmed_at');
    if (served) {
        out.push({text: 'Served ' + formatDate(served), kind: 'info'});
    } else if (form.status === 'sending') {
        out.push({text: 'Sending', kind: 'info'});
    } else if (form.sent_at || form.status === 'sent') {
        out.push({text: 'Sent' + (form.sent_at ? ' ' + formatDate(form.sent_at) : ''), kind: 'info'});
    }
    if (form.published_at || form.status === 'published') {
        out.push({text: 'In Buildium', kind: 'info'});
    } else if (form.status === 'publishing') {
        out.push({text: 'Publishing', kind: 'info'});
    }
    if (form.status === 'review') {
        out.push({text: 'Waiting for ' + (formMeta(form, 'review_by_name') || 'PM'), kind: 'warn'});
    }
    // RTB-7: whether the new rent reached Buildium (AddBuildiumRentLog reads Buildium back).
    if (isRentIncrease(form) && formMeta(form, 'increase_withdrawn')) {
        out.push({text: 'Increase withdrawn', kind: 'muted', title: 'Marked by ' + (formMeta(form, 'increase_withdrawn_by') || 'an administrator')});
    } else if (isRentIncrease(form) && formMeta(form, 'buildium_rent_error')) {
        out.push({text: 'Rent not in Buildium', kind: 'warn', title: formMeta(form, 'buildium_rent_error')});
    } else if (isRentIncrease(form) && formMeta(form, 'buildium_rent_posted_at')) {
        out.push({text: 'Rent in Buildium', kind: 'ok'});
    } else if (isRentIncrease(form) && (form.status === 'sending' || (form.sent_at && Date.parse(form.sent_at) > Date.parse('2026-10-19T19:07:00Z')))) {
        // Sent since sending started entering the rent (deployed 2026-10-19 12:07 PT) and no answer yet.
        out.push({text: 'Entering rent in Buildium…', kind: 'info'});
    }
    if (!out.length && formMeta(form, 'needs_in_person_service') && confirmed) {
        out.push({text: 'Confirmed · serve in person', kind: 'warn'});
    }
    if (!out.length && formMeta(form, 'held_for')) {
        out.push({text: 'Office check before release', kind: 'info'});
    }
    if (!out.length) {
        if (auto && !confirmed) out.push({text: 'Needs confirmation', kind: 'warn'});
        else if (auto && confirmed) out.push({text: 'Confirmed', kind: 'ok'});
        else if (form.status === 'draft') out.push({text: 'Draft', kind: 'muted'});
        else if (form.status) out.push({text: form.status.charAt(0).toUpperCase() + form.status.slice(1), kind: 'muted'});
    }
    return out;
}

/** Tenant email addresses the server will send to (SendSterlingForm reads tenants[].Email). */
export function tenantEmailsOf(form) {
    return tenantsOf(form).map(t => t.Email).filter(e => e && /@/.test(e));
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : (many || one + 's')}`;

/** ISO end date when the lease ends in Buildium before the notice takes effect (the list sends lease_to_date). */
export function leaseEndsBefore(form) {
    const eff = String(formMeta(form, 'rent_new_payable_date') || '');
    const m = eff.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    const iso = m ? `${m[3]}-${m[2]}-${m[1]}` : (/^\d{4}-\d{2}-\d{2}$/.test(eff) ? eff : '');
    return form.lease_to_date && iso && form.lease_to_date < iso ? form.lease_to_date : '';
}

/**
 * What an action on RTB-7 notices will do, in the words shown before the person presses the button.
 * One place, so the list, the side panel and the send dialog never describe the same action differently.
 * Returns {title, lines, button}.
 */
export function rtb7Effects(action, forms) {
    const list = forms || [];
    const withEmail = list.filter(f => tenantEmailsOf(f).length);
    const noEmail = list.length - withEmail.length;
    const n = list.length;
    const ending = list.filter(f => leaseEndsBefore(f)).length;
    const rentLine = 'The new rent is entered in Buildium as a rent schedule starting on the effective date, copying the current charge (GL account, memo, due day). Only when it is one monthly charge that matches the notice; anything else is left for the office, shown as "Rent not in Buildium" with the reason.'
        + (ending ? ` ${plural(ending, 'lease ends', 'leases end')} in Buildium before the effective date (fixed term without rollover): their rent is not entered automatically.` : '');
    if (action === 'confirm-send') {
        const lines = [`${plural(n, 'notice')} marked as confirmed by you.`];
        if (withEmail.length) {
            lines.push(`${plural(withEmail.length, 'notice')}: the tenants are emailed the notice (PDF attached) with the tenant email template, and the PDF is added to the lease's files in Buildium.`);
            lines.push(rentLine);
        }
        if (noEmail) {
            lines.push(`${plural(noEmail, 'notice')} with no tenant email: nothing is sent. Print and serve in person; recording the service in Sterling Hub enters the new rent in Buildium.`);
        }
        return {title: 'Confirm & send to tenants', lines, button: `Confirm & send ${n}`};
    }
    if (action === 'confirm') {
        return {
            title: 'Confirm only',
            lines: [`${plural(n, 'notice')} marked as confirmed.`, 'Nothing is sent to tenants and Buildium is not changed. Send, publish or serve them afterwards.'],
            button: `Confirm ${n}`,
        };
    }
    if (action === 'send') {
        const lines = [];
        if (withEmail.length) {
            lines.push(`${plural(withEmail.length, 'notice')}: the tenants are emailed the notice (PDF attached) with the template you choose, and the PDF is added to the lease's files in Buildium.`);
            lines.push(rentLine);
        }
        if (noEmail) lines.push(`${plural(noEmail, 'notice')} with no tenant email: nothing is sent and Buildium is not changed.`);
        return {title: 'Send by email', lines, button: 'Send'};
    }
    if (action === 'publish') {
        return {
            title: 'Publish to Buildium',
            lines: [
                `${plural(n, 'notice')}: the PDF is added to the lease's files in Buildium (a second copy if it is already there).`,
                rentLine,
                'Tenants are NOT emailed. Use this for notices already served in person or by mail.',
            ],
            button: `Publish ${n}`,
        };
    }
    if (action === 'withdraw') {
        return {
            title: 'Increase withdrawn',
            lines: [
                `${plural(n, 'notice')} marked as withdrawn: the increase was not applied (removed in Buildium or agreed with the tenant).`,
                'Nothing is sent to tenants and Buildium is not changed — remove any rent schedule there yourself if one exists.',
                'These leases no longer count as increased: the monthly run drafts them again once their current rent is 12 months old.',
            ],
            button: `Mark ${n} withdrawn`,
        };
    }
    if (action === 'skip') {
        return {
            title: 'No increase this year',
            lines: [`${plural(n, 'draft')} archived. Nothing is sent and Buildium is not changed.`, 'The monthly run drafts these leases again for the same date next year.'],
            button: `Skip ${n}`,
        };
    }
    return {title: '', lines: [], button: 'OK'};
}

/** The lines as HTML for an Element confirm box (escaped; dangerouslyUseHTMLString). */
export function effectsHtml(effects) {
    const esc = s => String(s).replace(/[&<>"]/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'}[c]));
    return '<p style="margin:0 0 6px">What happens:</p><ul style="margin:0;padding-left:18px">'
        + effects.lines.map(l => `<li style="margin-bottom:4px">${esc(l)}</li>`).join('') + '</ul>';
}
